import {
  parseChildrenArray,
  useModifySearchParam,
  useSearchParams,
} from '@/utils'
import { identity } from 'ramda'
import React, { useEffect, useRef, useState, ReactNode } from 'react'
import { applyTabInkStyles } from './helpers'
import { Container, TabInk, TabList, TabsWrapper } from './styles'
import { TabNode } from './TabNode'
import { ThemeColor } from '@/enums'

interface TabPanelProps {
  queryParamKey?: string
  children: ReactNode
  [key: string]: any
}

interface TabPanelBaseProps {
  defaultActiveKey?: string
  onChange?: (tabKey: string) => void
  children: ReactNode
  withContainer?: (children: ReactNode) => ReactNode
  color?: ThemeColor
}

interface TabPaneProps {
  badge?: number
  children: ReactNode
  text: React.ElementType | string
  key?: string
}

export type TabPanelRef = {
  children: {
    tabInk: HTMLElement
    tabWrapper: HTMLElement
  }
} & HTMLDivElement

export interface Tab {
  key: string
  [key: string]: any
  onClick?: () => void
}

export const TabPanel: React.FC<TabPanelProps> = ({
  queryParamKey = 'tab',
  onChange,
  defaultActiveKey,
  shouldChangeUrl = true,
  ...props
}) => {
  const queryParamValue = useSearchParams()
  const changeStepParam = useModifySearchParam()

  const handleTabChange = (tabKey: string) => {
    if (shouldChangeUrl) {
      changeStepParam({ [queryParamKey]: tabKey })
    }
    onChange?.(tabKey)
  }

  return (
    <TabPanelBase
      {...props}
      defaultActiveKey={defaultActiveKey || queryParamValue[queryParamKey]}
      onChange={handleTabChange}
    />
  )
}

export const TabPanelBase: React.FC<TabPanelBaseProps> = ({
  defaultActiveKey,
  onChange,
  children,
  withContainer = identity,
  color = ThemeColor.b50,
}) => {
  const tabPanelRef = useRef<TabPanelRef>(null)
  const tabs = parseChildrenArray(children) as Tab[]
  const [activeKey, setActiveKey] = useState<string | undefined>(
    defaultActiveKey || tabs[0]?.key,
  )

  useEffect(() => {
    setActiveKey(defaultActiveKey)
  }, [defaultActiveKey])

  const activeTab = tabs.find((tab) => tab.key === activeKey) || tabs[0]

  const handleTabClick = (key: string) => {
    if (key !== activeKey) {
      setActiveKey(key)
      onChange?.(key)
    }
  }

  useEffect(() => {
    if (tabPanelRef.current && activeKey) {
      applyTabInkStyles(tabPanelRef.current, tabs, activeKey)
    }
  }, [activeKey, tabs])

  return (
    <Container>
      <TabList ref={tabPanelRef}>
        <TabsWrapper>
          {tabs.map(({ key, text }) => (
            <TabNode
              key={key}
              active={activeTab?.key === key}
              handleClick={handleTabClick}
              tabKey={key || ''}
              text={text}
              color={color}
            />
          ))}
        </TabsWrapper>
        <TabInk color={color} />
      </TabList>
      {withContainer(activeTab?.children)}
    </Container>
  )
}

export const TabPane: React.FC<TabPaneProps> = () => null
